import { useMemo, useRef, useEffect } from "react";
import { useQuery as useZeroQuery } from "@rocicorp/zero/react";
import { appQueries } from "@production-hub/shared";
import { formatProjectName } from "@/lib/format-project-name";

export type SlashCommandCategory =
  | "project"
  | "project_group"
  | "meeting_note"
  | "external_link";

export interface SlashCommandItem {
  id: string;
  label: string;
  category: SlashCommandCategory;
  route: string;
}

type ProjectSlashCommandRow = {
  id: string;
  name: string;
  number?: string | number | null;
  eventStartDate?: string | number | null;
};

type ProjectGroupSlashCommandRow = {
  id: string;
  name: string;
  number?: string | null;
};

type MeetingNoteSlashCommandRow = {
  id: string;
  title: string;
  adminOnly?: boolean | null;
};

type ZeroQueryArg = Parameters<typeof useZeroQuery>[0];

type CommandItemQueries = {
  projects: { commandItems: () => ZeroQueryArg };
  projectGroups: { commandItems: () => ZeroQueryArg };
  meetingNotes: { commandItems: () => ZeroQueryArg };
};

/**
 * Managers-only notes are only offered when editing managers-only content:
 * a link stores the note's title, which would leak it into team content.
 */
export function useSlashCommandItems({
  includeManagersOnly = false,
}: { includeManagersOnly?: boolean } = {}) {
  const commandItemQueries = appQueries as unknown as CommandItemQueries;
  const [projectsData] = useZeroQuery(
    commandItemQueries.projects.commandItems(),
  );
  const [projectGroupsData] = useZeroQuery(
    commandItemQueries.projectGroups.commandItems(),
  );
  const [meetingNotesData] = useZeroQuery(
    commandItemQueries.meetingNotes.commandItems(),
  );

  const items = useMemo<SlashCommandItem[]>(() => {
    const result: SlashCommandItem[] = [];
    const projects = (projectsData ?? []) as ProjectSlashCommandRow[];
    const projectGroups = (projectGroupsData ??
      []) as ProjectGroupSlashCommandRow[];
    const meetingNotes = (meetingNotesData ??
      []) as MeetingNoteSlashCommandRow[];

    for (const p of projects) {
      const projectName = formatProjectName(p.name, p.eventStartDate ?? null);
      result.push({
        id: p.id,
        label: p.number ? `${p.number} – ${projectName}` : projectName,
        category: "project",
        route: `/projects/${p.id}`,
      });
    }

    for (const g of projectGroups) {
      result.push({
        id: g.id,
        label: g.number ? `${g.number} – ${g.name}` : g.name,
        category: "project_group",
        route: `/projects/groups/${g.id}`,
      });
    }

    for (const n of meetingNotes) {
      if (n.adminOnly && !includeManagersOnly) continue;
      result.push({
        id: n.id,
        label: n.title,
        category: "meeting_note",
        route: `/meeting-notes/${n.id}`,
      });
    }

    result.push({
      id: "__external_link__",
      label: "External Link...",
      category: "external_link",
      route: "",
    });

    return result;
  }, [projectsData, projectGroupsData, meetingNotesData, includeManagersOnly]);

  const itemsRef = useRef(items);
  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  return { items, itemsRef };
}
